import React from "react";
import ServiceLandingPage, {
  InternalLink,
} from "../components/ServiceLandingPage";
import { COMPANY } from "../content";

const SCOPE = [
  {
    name: "Single-contract turnkey delivery",
    desc: "One contract, one project lead, one accountable team — covering design coordination, demolition, civil, MEP, joinery, finishes, FF&E and final handover across the UAE.",
  },
  {
    name: "Civil works & structural alterations",
    desc: "Demolition, partition walls, plastering, waterproofing, screeding and floor levelling — handled by our own UAE-based civil team.",
  },
  {
    name: "MEP coordination",
    desc: "Mechanical, electrical and plumbing — new circuits, AC re-zoning, fire-life-safety integration and authority sign-off across DEWA, SEWA, DM, DCD, JAFZA and DMCC.",
  },
  {
    name: "Bespoke joinery & furniture (in-house)",
    desc: "Wardrobes, kitchens, vanities, workstations, boardroom tables and built-in furniture manufactured at our Sharjah atelier — no third-party supply risk.",
  },
  {
    name: "Premium finishes & lighting",
    desc: "Marble, microcement, engineered wood, decorative wallpaper, fluted panels, cove lighting and designer fixtures — sourced and installed to villa- or HQ-grade specification.",
  },
  {
    name: "Approvals, FF&E & handover",
    desc: "Authority and community approvals, furniture procurement, soft furnishings, art curation, deep clean, snag, commissioning and a defects-warranty period.",
  },
];

const PROCESS = [
  {
    n: "01",
    title: "Brief & site visit",
    body: "We visit your site (villa, apartment, office or commercial space), align on scope, take measurements and listen to how the space needs to function.",
  },
  {
    n: "02",
    title: "Design, BOQ & contract",
    body: "Concept design, finish boards, FF&E selections and an itemised BOQ — consolidated into a single contract and a fixed handover date.",
  },
  {
    n: "03",
    title: "Approvals & mobilisation",
    body: "Authority approvals (DM, Trakheesi, DCD, DMCC, JAFZA, etc.), community NOCs, deposits, access scheduling, site protection and team mobilisation.",
  },
  {
    n: "04",
    title: "Phased on-site execution",
    body: "Demolition → civil → MEP first-fix → joinery → finishes → second-fix → FF&E install → snag — coordinated by one project lead, not a chain of subcontractors.",
  },
  {
    n: "05",
    title: "Snag, handover & warranty",
    body: "Final snag, deep clean, walkthrough, commissioning and a documented defects-warranty period — we stay accountable after handover, not just before.",
  },
];

const FAQS = [
  {
    q: "What is a turnkey contractor?",
    a: "A turnkey contractor takes responsibility for the entire project under one contract — design coordination, civil works, MEP, joinery, furniture, finishes, approvals and final handover — so the client receives a fully completed, ready-to-use space with one point of accountability instead of managing multiple separate trades.",
  },
  {
    q: "How is turnkey different from hiring separate contractors?",
    a: "With separate contractors the client carries the coordination risk — programme delays, scope gaps, blame-shifting and budget overruns when one trade is late or out of spec. A turnkey contractor absorbs all of that under one fixed-price contract with one project lead, so the client deals with a single accountable team and a single handover date.",
  },
  {
    q: "Where in the UAE do you deliver projects?",
    a: "Time Furniture delivers turnkey contracting projects across Dubai, Abu Dhabi, Sharjah, Ajman, Ras Al Khaimah and Fujairah — including all major free zones and developer communities. Our core team and atelier are UAE-based, which means same-week site visits and direct accountability anywhere in the country.",
  },
  {
    q: "How much does a turnkey contractor charge in the UAE?",
    a: "Turnkey contracting in the UAE typically ranges from AED 180–1,500 per sq.ft depending on category — commercial fit-out, residential renovation, premium villa, or executive HQ. Time Furniture provides transparent itemised BOQs after a free site visit, so the budget is tied to a clear specification before signing.",
  },
  {
    q: "Do you handle authority and community approvals?",
    a: "Yes. As part of every turnkey contract we coordinate Dubai Municipality, Trakheesi, Dubai Civil Defence, JAFZA, DMCC, DIFC, Dubai South, ADGM, SEWA and developer NOCs (Emaar, Nakheel, Meraas, Damac, Aldar) — including consultant drawings, deposits and access scheduling.",
  },
];

const WHY = [
  {
    t: "30+ years of single-contract delivery",
    d: "Established in 1993. Three decades of UAE projects under one-contract execution — fewer surprises, faster decisions, no coordination gaps.",
  },
  {
    t: "In-house team, end-to-end",
    d: "Civil, MEP, joinery, upholstery, curtains and finishing — all delivered by our own UAE team. We don't subcontract the work that defines our standard.",
  },
  {
    t: "Transparent BOQ, fixed dates",
    d: "Itemised quotations, transparent material costs, a fixed handover date and weekly progress reporting — so finance and ownership can plan with confidence.",
  },
];

export default function TurnkeyContractorUAE() {
  return (
    <ServiceLandingPage
      testid="turnkey-contractor-uae-page"
      whatsappText={COMPANY.whatsappTurnkeyText}
      pageTitle="Turnkey Contractor UAE | Single-Contract Interior & Fit-Out Specialists — Time Furniture"
      metaDescription="Leading turnkey contractor in the UAE. Single-contract interior fit-out, renovation and commercial fit-out — civil, MEP, joinery, furniture and finishing under one team since 1993."
      canonical="https://timefurniture.ae/turnkey-contractor-uae"
      serviceSchemaName="Turnkey Interior Contracting — UAE"
      breadcrumbLabel="Turnkey Contractor UAE"
      eyebrow="Turnkey Contractor · UAE"
      h1="Turnkey Contractor in the"
      h1Accent="UAE"
      intro="Time Furniture is a single-contract turnkey contractor in the UAE delivering interior fit-out, renovation and commercial projects across Dubai, Abu Dhabi, Sharjah and the wider Emirates. Since 1993, we've handed over villas, apartments, offices, clinics, hospitality lounges and holiday homes — managing every phase under one accountable team: design coordination, demolition, civil works, MEP, bespoke joinery, custom furniture, marble, lighting, curtains, FF&E, authority approvals and final handover. Clients work with one project lead, sign one fixed-price contract, receive a transparent itemised BOQ tied to a fixed handover date, and walk into a fully completed, move-in-ready space — not a structurally finished shell still waiting on six different specialists. Three decades of UAE delivery means fewer surprises, faster site decisions, and no coordination gaps."
      scope={SCOPE}
      process={PROCESS}
      whyChoose={WHY}
      faqs={FAQS}
      ctaHeading="Need a"
      ctaAccent="turnkey contractor in the UAE"
      ctaSubtext="Tell us your scope, location and target handover date. We'll review and reply with an indicative timeline and budget — under one accountable contract."
      internalLinksLine={
        <>
          See our{" "}
          <InternalLink to="/interior-fit-out-dubai">Dubai interior fit-out</InternalLink>
          ,{" "}
          <InternalLink to="/villa-renovation-dubai">villa renovation</InternalLink>
          ,{" "}
          <InternalLink to="/office-fit-out-dubai">office fit-out</InternalLink>{" "}
          pages, or browse{" "}
          <InternalLink to="/portfolio">completed UAE projects</InternalLink>
          .
        </>
      }
    />
  );
}
